import { DateTimeFormatter, DayOfWeek } from "@js-joda/core";
import assertNever from "assert-never";
import React from "react";
import {
  FlexibleVisitParams,
  OnboardingVisitInstanceId,
  PublicOnboardingVisitDetails,
  PublicVisitBroadcastDetails,
  VisitInstanceId,
} from "../messages";
import { STRINGS } from "../strings";
import { VisitType } from "../system/AppState/AppVisitsState";
import { globalize, translate } from "../translate";
import { getShowSelectAllCheckbox } from "../utils/flexibleVisitUtils";
import { HashSet } from "../utils/HashSet";
import { CheckedVisitData } from "./AvailableVisitActivity";
import AvailableVisitDates from "./AvailableVisitDates";
import { AppCard, AppCardContent, AppCardHeader, AppCardTitle } from "./Generic/AppCard";
import { AppList } from "./Generic/AppList";
import Flex from "./Generic/Flex";
import { If } from "./Generic/If";
import { CheckedOnboardingVisitData } from "./OnboardingVisitActivity";
import NotesText from "./Patient/components/NotesText";
import { FlexibleVisitProps } from "./useFlexibleVisit";
import WarningCard from "./WarningCard/WarningCard";

interface PropsTypes {
  Onboarding: {
    checkedVisitData: CheckedOnboardingVisitData;
    visitInstanceId: OnboardingVisitInstanceId;
  };
  Available: {
    checkedVisitData: CheckedVisitData;
    visitInstanceId: VisitInstanceId;
  };
}

interface Props<TVisitType extends VisitType> {
  broadcastDetails: PublicVisitBroadcastDetails | PublicOnboardingVisitDetails;
  checkedVisits: HashSet<PropsTypes[TVisitType]["visitInstanceId"]>;
  checkedPatterns: HashSet<string>;
  requestedVisitBatch: boolean;
  areAllShiftsChecked: boolean;
  visitType: TVisitType;
  flexibleVisitProps: FlexibleVisitProps | null;
  onCheckVisit: (visitData: PropsTypes[TVisitType]["checkedVisitData"]) => void;
}

function AvailableVisitDatesAppCard<TVisitType extends VisitType>(props: Props<TVisitType>) {
  const showSelectAll = getShowSelectAllCheckbox(props.broadcastDetails);
  const choiceCounterHeaderTitle = getChoiceCounterHeaderTitle(
    props.broadcastDetails,
    props.flexibleVisitProps
  );

  return (
    <AppCard>
      <AppCardHeader>
        <AppCardTitle>
          {props.broadcastDetails.type === "FlexiblePattern" ? (
            <HeaderForFlexibleVisit
              flexibleVisitParams={props.broadcastDetails.flexibleVisitParams}
              headerText={choiceCounterHeaderTitle}
            />
          ) : (
            translate(STRINGS.VISIT_SCHEDULE)
          )}
        </AppCardTitle>
      </AppCardHeader>
      <AppCardContent>
        <If condition={!props.broadcastDetails.patientIsActive}>
          <WarningCard
            type="info"
            noShadow={true}
            message={translate(STRINGS.PATIENT_PENDING_APPROVAL_START_DATE_MIGHT_CHANGE)}
            readonly={true}
          />
        </If>
        <AppList>
          <AvailableVisitDates
            allowMultipleCaregivers={
              "allowMultipleCaregivers" in props.broadcastDetails &&
              props.broadcastDetails.allowMultipleCaregivers
            }
            broadcastDetails={props.broadcastDetails}
            onCheckVisit={props.onCheckVisit}
            checkedVisits={props.checkedVisits}
            checkedPatterns={props.checkedPatterns}
            requestedVisitBatch={props.requestedVisitBatch}
            areAllShiftsChecked={props.areAllShiftsChecked}
            visitType={props.visitType}
            flexibleVisitProps={props.flexibleVisitProps}
            showSelectAll={showSelectAll}
            choiceCounterHeaderTitle={choiceCounterHeaderTitle}
          />
        </AppList>
      </AppCardContent>
    </AppCard>
  );
}

function getHoursLeftToSelectTitleText(params: {
  flexibleVisitParams: FlexibleVisitParams;
  hoursInWeekLeft: number | null;
  daysInWeekLeft: number | null;
}) {
  switch (params.flexibleVisitParams.type) {
    case "PermanentFlexibleHoursAndDaysPerWeek":
    case "PermanentFlexibleHoursFixedDaysPerWeek":
      return params.hoursInWeekLeft === null
        ? null
        : params.hoursInWeekLeft === 0
        ? translate(STRINGS.X_HOURS_SELECTED).replace(
            "%x%",
            params.flexibleVisitParams.amountOfHoursPerWeek.toString()
          )
        : translate(STRINGS.X_HOURS_TO_SELECT).replace("%x%", params.hoursInWeekLeft.toString());

    case "SingleFlexible":
      return params.hoursInWeekLeft === null
        ? null
        : params.hoursInWeekLeft === 0
        ? translate(STRINGS.X_HOURS_SELECTED).replace(
            "%x%",
            params.flexibleVisitParams.amountOfHours.toString()
          )
        : translate(STRINGS.X_HOURS_TO_SELECT).replace("%x%", params.hoursInWeekLeft.toString());

    case "PermanentFixedHoursFlexibleDays":
      return params.daysInWeekLeft === null
        ? null
        : params.daysInWeekLeft === 0
        ? translate(STRINGS.X_DAYS_SELECTED).replace(
            "%x%",
            params.flexibleVisitParams.amountOfDays.toString()
          )
        : translate(STRINGS.X_DAYS_TO_SELECT).replace("%x%", params.daysInWeekLeft.toString());

    case "PermanentFlexibleHoursAndDaysPattern":
    case "PermanentFlexibleHoursFixedDaysPattern":
      return null;

    default:
      assertNever(params.flexibleVisitParams, true);
  }
}

interface HeaderProps {
  flexibleVisitParams: FlexibleVisitParams;
  headerText: string | null;
}

const HeaderForFlexibleVisit = (props: HeaderProps) => {
  const explainVisitText = getFlexibleVisitExplanationText(props.flexibleVisitParams);
  return (
    <Flex direction="column" gap={10}>
      <div>{translate(STRINGS.SELECT_YOUR_SCHEDULE)}</div>
      <NotesText>
        <div style={{ whiteSpace: "pre-line" }}>{explainVisitText}</div>
      </NotesText>
    </Flex>
  );
};

const getFlexibleVisitExplanationText = (flexibleVisitParams: FlexibleVisitParams) => {
  switch (flexibleVisitParams.type) {
    case "PermanentFixedHoursFlexibleDays": {
      const includedShifts = flexibleVisitParams.daysToChooseFrom.filter(
        (day) => !flexibleVisitParams.excludedDaysFromCaregivers.includes(day.day)
      );

      if (includedShifts.length === 0) {
        return "";
      }

      const startTime = includedShifts[0].startTime.format(DateTimeFormatter.ofPattern("HH:mm"));
      const endTime = includedShifts[0].endTime.format(DateTimeFormatter.ofPattern("HH:mm"));

      const maxNumberOfShifts = Math.min(includedShifts.length, flexibleVisitParams.amountOfDays);

      return translate(STRINGS.FLEXIBLE_DAYS_PER_WEEK_EXPLANATION_TEXT)
        .replace("{NUM_OF_SHIFTS}", maxNumberOfShifts.toString())
        .replace("{START_TIME}", startTime)
        .replace("{END_TIME}", endTime);
    }
    case "PermanentFlexibleHoursAndDaysPattern": {
      return mapPermanentFlexibleHoursAndDaysPatternDetailsToExplanationText(
        flexibleVisitParams.flexibleDetails
      );
    }
    case "PermanentFlexibleHoursAndDaysPerWeek": {
      return translate(STRINGS.FLEXIBLE_DAYS_AND_HOURS_PER_WEEK_EXPLANATION_TEXT).replace(
        "{HOURS_PER_WEEK}",
        flexibleVisitParams.amountOfHoursPerWeek.toString()
      );
    }
    case "PermanentFlexibleHoursFixedDaysPattern": {
      const shiftsStrings = mapPermanentFlexibleHoursFixedDaysPatternDetailsToShiftsPatternString(
        flexibleVisitParams.excludedDaysFromCaregivers,
        flexibleVisitParams.details
      );
      if (shiftsStrings.length === 1) {
        return translate(STRINGS.FLEXIBLE_HOURS_FIXED_DAYS_ONE_PATTERN_EXPLANATION_TEXT).replace(
          "{SHIFTS_TO_CHOOSE}",
          shiftsStrings[0]
        );
      }

      const schedulePattern = shiftsStrings.slice(0, shiftsStrings.length - 1).join(", ");
      const lastPattern = shiftsStrings[shiftsStrings.length - 1];

      return translate(STRINGS.FLEXIBLE_HOURS_FIXED_DAYS_PATTERN_EXPLANATION_TEXT)
        .replace("{SHIFTS_TO_CHOOSE}", schedulePattern)
        .replace("{LAST_SHIFT_TO_CHOOSE}", lastPattern);
    }
    case "SingleFlexible":
      return translate(STRINGS.SINGLE_FLEXIBLE_VISIT_EXPLANATION)
        .replace("{{durationHours}}", flexibleVisitParams.amountOfHours.toString())
        .replace("{{date}}", globalize().formatLocalDate(flexibleVisitParams.date));

    case "PermanentFlexibleHoursFixedDaysPerWeek": {
      if (flexibleVisitParams.days.length === 1) {
        const daysString = translate(STRINGS.FLEXIBLE_VISIT_DAY_TO_CHOOSE).replace(
          "{DAY}",
          formatDayOfWeek(flexibleVisitParams.days[0])
        );

        return translate(STRINGS.FLEXIBLE_HOURS_ONE_DAY_EXPLANATION_TEXT).replace(
          "{DAYS_TO_CHOOSE}",
          daysString
        );
      }

      const includedDays = flexibleVisitParams.days.filter(
        (day) => !flexibleVisitParams.excludedDaysFromCaregivers.includes(day)
      );
      const daysToChoose = includedDays
        .slice(0, includedDays.length - 1)
        .map((day) =>
          translate(STRINGS.FLEXIBLE_VISIT_DAY_TO_CHOOSE).replace("{DAY}", formatDayOfWeek(day))
        );
      const lastDayToChooseFrom = includedDays[includedDays.length - 1];

      return translate(STRINGS.FLEXIBLE_HOURS_EXPLANATION_TEXT)
        .replace("{DAYS_TO_CHOOSE}", daysToChoose.join(", "))
        .replace("{LAST_DAY_TO_CHOOSE}", formatDayOfWeek(lastDayToChooseFrom));
    }
  }
};

function formatDayOfWeek(dayOfWeek: DayOfWeek) {
  return globalize().formatDayOfWeek(dayOfWeek, false);
}

function mapPermanentFlexibleHoursFixedDaysPatternDetailsToShiftsPatternString(
  excludedShifts: DayOfWeek[],
  details: {
    day: DayOfWeek;
    amountOfHours: number;
  }[]
) {
  const shiftsByNumOfHours = new Map<number, DayOfWeek[]>();

  for (const shift of details) {
    if (excludedShifts.includes(shift.day)) {
      continue;
    }

    const daysByNumOfHours = shiftsByNumOfHours.get(shift.amountOfHours);
    if (daysByNumOfHours === undefined) {
      shiftsByNumOfHours.set(shift.amountOfHours, [shift.day]);
    } else {
      daysByNumOfHours.push(shift.day);
      shiftsByNumOfHours.set(shift.amountOfHours, [...daysByNumOfHours]);
    }
  }

  const shiftsStrings: string[] = [];

  for (const [numOfHours, days] of shiftsByNumOfHours) {
    shiftsStrings.push(
      translate(STRINGS.FLEXIBLE_HOURS_PATTERN_SHIFT)
        .replace("{NUM_OF_HOURS}", numOfHours.toString())
        .replace(
          "{DAY_OF_WEEK}",
          days
            .map((day) =>
              translate(STRINGS.FLEXIBLE_VISIT_DAY_TO_CHOOSE).replace("{DAY}", formatDayOfWeek(day))
            )
            .join(", ")
        )
    );
  }
  return shiftsStrings;
}

function mapPermanentFlexibleHoursAndDaysPatternDetailsToExplanationText(
  details: {
    amountOfDays: number;
    amountOfHours: number;
  }[]
) {
  if (details.length === 1) {
    const baseString =
      details[0].amountOfDays > 1
        ? STRINGS.FLEXIBLE_HOURS_AND_DAYS_PATTERN_SHIFT
        : STRINGS.FLEXIBLE_HOURS_AND_DAYS_PATTERN_SINGLE_SHIFT;
    const patternString = translate(baseString)
      .replace("{NUM_OF_SHIFTS}", details[0].amountOfDays.toString())
      .replace("{NUM_OF_HOURS}", details[0].amountOfHours.toString());

    return translate(STRINGS.FLEXIBLE_HOURS_ONE_PATTERN_EXPLANATION_TEXT).replace(
      "{SHIFTS_TO_CHOOSE}",
      patternString
    );
  }

  let totalNumberOfShifts = 0;
  details.forEach((details) => (totalNumberOfShifts += details.amountOfDays));

  const schedulePattern = details
    .map((pattern) =>
      translate(
        pattern.amountOfDays > 1
          ? STRINGS.FLEXIBLE_HOURS_AND_DAYS_PATTERN_SHIFT
          : STRINGS.FLEXIBLE_HOURS_AND_DAYS_PATTERN_SINGLE_SHIFT
      )
        .replace("{NUM_OF_SHIFTS}", pattern.amountOfDays.toString())
        .replace("{NUM_OF_HOURS}", pattern.amountOfHours.toString())
    )
    .slice(0, details.length - 1)
    .join(", ");

  const lastPattern = details[details.length - 1];
  const baseLastPatternString =
    lastPattern.amountOfDays > 1
      ? STRINGS.FLEXIBLE_HOURS_AND_DAYS_PATTERN_SHIFT
      : STRINGS.FLEXIBLE_HOURS_AND_DAYS_PATTERN_SINGLE_SHIFT;
  const lastPaternString = translate(baseLastPatternString)
    .replace("{NUM_OF_SHIFTS}", lastPattern.amountOfDays.toString())
    .replace("{NUM_OF_HOURS}", lastPattern.amountOfHours.toString());

  return translate(STRINGS.FLEXIBLE_HOURS_AND_DAYS_PATTERN_EXPLANATION_TEXT)
    .replace("{TOTAL_SHIFTS_NUMBER}", totalNumberOfShifts.toString())
    .replace("{SHIFTS_TO_CHOOSE}", schedulePattern)
    .replace("{LAST_SHIFT_TO_CHOOSE}", lastPaternString);
}

function getChoiceCounterHeaderTitle(
  broadcastDetails: PublicVisitBroadcastDetails | PublicOnboardingVisitDetails,
  flexibleVisitProps: FlexibleVisitProps | null
) {
  if (broadcastDetails.type !== "FlexiblePattern") {
    return null;
  }

  return broadcastDetails.allowMultipleCaregivers && flexibleVisitProps !== null
    ? getSelectedHoursCounterTitleText(
        broadcastDetails.flexibleVisitParams.type,
        flexibleVisitProps
      )
    : getHoursLeftToSelectTitleText({
        flexibleVisitParams: broadcastDetails.flexibleVisitParams,
        hoursInWeekLeft: flexibleVisitProps?.hoursInWeekLeft ?? null,
        daysInWeekLeft: flexibleVisitProps?.daysInWeekLeft ?? null,
      });
}

function getSelectedHoursCounterTitleText(
  visitType: FlexibleVisitParams["type"],
  flexibleVisitProps: FlexibleVisitProps
) {
  const slected = [...flexibleVisitProps.daysMap.entries()].filter(
    ([_day, data]) => data.isChecked
  );

  switch (visitType) {
    case "PermanentFlexibleHoursAndDaysPerWeek":
    case "PermanentFlexibleHoursFixedDaysPerWeek": {
      const hoursSelected = slected.reduce((acc, [_, data]) => {
        const selectedDuration = data.durations.find((d) => d.isSelected);
        return acc + (selectedDuration ? selectedDuration.numberOfHours : 0);
      }, 0);

      return translate(STRINGS.X_HOURS_SELECTED).replace("%x%", hoursSelected.toString());
    }
    case "PermanentFixedHoursFlexibleDays":
      return translate(STRINGS.X_DAYS_SELECTED).replace("%x%", slected.length.toString());
    case "PermanentFlexibleHoursAndDaysPattern":
    case "PermanentFlexibleHoursFixedDaysPattern":
    case "SingleFlexible":
      return null;
  }
}

export default AvailableVisitDatesAppCard;
